import { useCallback, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Gem } from "lucide-react";

import {
    Sidebar as SidebarPrimitive,
    SidebarContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarSeparator,
    SidebarTrigger,
    SIDEBAR_WIDTH,
    useSidebar,
} from "components/ui/sidebar";
import { cn } from "lib/utils";
import { useGroups } from "features/Settings/General/OrganisationLevelGroups/context";
import { useUser } from "utils/hooks/user";
import { hasAccessInList } from "utils/hooks/access";
import { useSidebarConfig } from "./config";
import "./style.scss";

const getMenuKeys = (config, basePath = "") => {
    return config
        .map((item) => {
            if (item.children) {
                const url = `${basePath}/${item.path}`;
                return [url, ...getMenuKeys(item.children, url)];
            }
            return null;
        })
        .flat(Infinity)
        .filter((key) => key !== null);
};

function Sidebar() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { groups } = useGroups();
    const config = useSidebarConfig();
    const user = useUser();
    const accessMap = user?.permissions;

    const { state, isMobile } = useSidebar();
    const [hovering, setHovering] = useState(false);

    const collapsedIcon = state === "collapsed" && !isMobile;
    const showLabels = !collapsedIcon || hovering;

    const [openKeys, setOpenKeys] = useState(() =>
        getMenuKeys(config).filter((path) => pathname.includes(path))
    );

    const areAllKeysOpen = useMemo(
        () => getMenuKeys(config).every((key) => openKeys.includes(key)),
        [config, openKeys]
    );

    const toggleOpenKeys = useCallback(() => {
        setOpenKeys(areAllKeysOpen ? [] : getMenuKeys(config));
    }, [areAllKeysOpen, config]);

    const onMenuClick = useCallback(
        (key, groupOnly = false) => {
            setOpenKeys((prev) => {
                if (prev.includes(key)) {
                    if (pathname !== key && !pathname.startsWith(key + "/") && !groupOnly) {
                        return prev;
                    }
                    return prev.filter((item) => item !== key);
                }
                return prev.concat(key);
            });
        },
        [pathname]
    );

    const getItemClickHandler = useCallback(
        (isUrlWrapper, seperateWrapperFromLink, children, url) => (e) => {
            const isAtExactUrl = pathname === url;
            const isAtSubUrl = pathname.startsWith(url + "/");
            const hasIndexInSidebarFalse =
                !isUrlWrapper &&
                !seperateWrapperFromLink &&
                !!children?.find(({ index, inSidebar }) => index && inSidebar === false);

            if (hasIndexInSidebarFalse) {
                if (isAtExactUrl) {
                    e.preventDefault();
                }
                if (children && !isAtSubUrl) {
                    onMenuClick(url, false);
                }
                return;
            }

            const isAtUrl = isAtExactUrl || isAtSubUrl;
            const isToggleOnly = isUrlWrapper || seperateWrapperFromLink || isAtUrl;
            if (isToggleOnly && children) {
                e.preventDefault();
            }
            if (children) {
                onMenuClick(url, isToggleOnly);
            }
        },
        [onMenuClick, pathname]
    );

    const renderMenuItems = useCallback(
        (items, basePath = "", depth = 0) => {
            const rendered = [];

            for (const item of items) {
                const {
                    path,
                    title,
                    type,
                    icon: Icon,
                    permissions,
                    access,
                    children,
                    inSidebar = true,
                    index,
                } = item;
                const url = `${basePath}/${index ? path || "" : path}`;

                if (path === ":id/levels") {
                    rendered.push(
                        ...renderMenuItems(
                            groups.map((group) => ({
                                path: `settings/general/groups/${group.id}/levels`,
                                title: group.code,
                                icon: Gem,
                                permissions,
                            })),
                            "",
                            depth
                        )
                    );
                    continue;
                }

                if (!inSidebar) {
                    if (children) {
                        rendered.push(...renderMenuItems(children, url, depth));
                    }
                    continue;
                }

                if (type === "divider") {
                    if (!access) {
                        rendered.push(<SidebarSeparator key={`${url}-divider`} className="tw-my-1" />);
                    }
                    continue;
                }

                if (type === "title") {
                    if (!access && showLabels) {
                        rendered.push(
                            <div
                                key={`${url}-title`}
                                className="tw-px-3 tw-pb-1 tw-pt-3 tw-text-xs tw-font-semibold tw-uppercase tw-text-muted-foreground"
                            >
                                {t(title)}
                            </div>
                        );
                    }
                    continue;
                }

                const hidden = children
                    ? !access
                    : accessMap && !hasAccessInList(accessMap, permissions);

                if (hidden) {
                    continue;
                }

                const isUrlWrapper = children && !children.find(({ index }) => index);
                const indexChild = children?.find(({ index }) => index);
                const seperateWrapperFromLink = !!(indexChild && indexChild.inSidebar);
                const isClickable = !(isUrlWrapper || seperateWrapperFromLink);
                const open = openKeys.includes(url);
                const label = t(title) || t(path);
                const onClick = getItemClickHandler(isUrlWrapper, seperateWrapperFromLink, children, url);
                const isActive = pathname === url || pathname.startsWith(`${url}/`);

                const buttonContent = (
                    <>
                        {Icon && <Icon />}
                        {showLabels && <span className="tw-truncate">{label}</span>}
                        {children && showLabels && (
                            open ? (
                                <ChevronUp className="tw-ml-auto tw-size-3.5 tw-shrink-0" />
                            ) : (
                                <ChevronDown className="tw-ml-auto tw-size-3.5 tw-shrink-0" />
                            )
                        )}
                    </>
                );

                rendered.push(
                    <SidebarMenuItem key={url}>
                        <SidebarMenuButton
                            asChild={isClickable}
                            isActive={isActive}
                            onClick={isClickable ? undefined : onClick}
                            tooltip={collapsedIcon && !hovering ? label : undefined}
                            className={cn(
                                "tw-text-[color:var(--sidebar-text)] hover:tw-bg-[color:var(--sidebar-hover-bg)] hover:tw-text-[color:var(--sidebar-text-active)] [&>svg]:tw-text-[color:var(--sidebar-text)]",
                                isActive &&
                                    "tw-bg-[color:var(--sidebar-active-bg)] tw-text-[color:var(--sidebar-text-active)] [&>svg]:tw-text-[color:var(--sidebar-text-active)]",
                                !showLabels && "tw-size-9 tw-mx-auto tw-justify-center tw-p-0",
                                children && "tw-mb-1"
                            )}
                            style={depth > 0 && showLabels ? { paddingLeft: `${0.75 + depth * 0.9}rem` } : undefined}
                        >
                            {isClickable ? (
                                <NavLink to={url} onClick={onClick} end>
                                    {buttonContent}
                                </NavLink>
                            ) : (
                                buttonContent
                            )}
                        </SidebarMenuButton>

                        {children && (
                            <div
                                className={cn(
                                    "tw-ml-5 tw-grid tw-transition-[grid-template-rows] tw-duration-200 tw-ease-in-out",
                                    open && showLabels ? "tw-grid-rows-[1fr]" : "tw-grid-rows-[0fr]"
                                )}
                            >
                                <div className="tw-overflow-hidden">
                                    <SidebarMenu>{renderMenuItems(children, url, depth + 1)}</SidebarMenu>
                                </div>
                            </div>
                        )}
                    </SidebarMenuItem>
                );
            }

            return rendered;
        },
        [openKeys, groups, accessMap, t, pathname, getItemClickHandler, showLabels, collapsedIcon, hovering]
    );

    return (
        <SidebarPrimitive
            collapsible="icon"
            className="simpli-sidebar"
            onMouseEnter={() => collapsedIcon && setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            style={hovering && collapsedIcon ? { width: SIDEBAR_WIDTH, zIndex: 20, boxShadow: "0 10px 30px rgba(0,0,0,0.15)" } : undefined}
        >
            <SidebarHeader
                className={cn(
                    "tw-flex tw-items-center tw-py-5",
                    showLabels ? "tw-flex-row tw-justify-between tw-px-4" : "tw-flex-col tw-gap-3 tw-px-2"
                )}
            >
                {showLabels && (
                    <img
                        onClick={() => navigate("/")}
                        className="tw-h-9 tw-cursor-pointer"
                        src="/assets/images/logo-horizontal.png"
                        alt="logo"
                    />
                )}
                <SidebarTrigger />
            </SidebarHeader>

            <SidebarContent className={cn("tw-pb-4", showLabels ? "tw-px-4" : "tw-px-2")}>
                <SidebarMenu>
                    <SidebarMenuItem className="tw-mb-1">
                        <SidebarMenuButton
                            onClick={toggleOpenKeys}
                            tooltip={collapsedIcon && !hovering ? (areAllKeysOpen ? t("collapse-all") : t("expand-all")) : undefined}
                            className={cn(
                                "tw-text-[color:var(--sidebar-text)] hover:tw-bg-[color:var(--sidebar-hover-bg)] hover:tw-text-[color:var(--sidebar-text-active)]",
                                !showLabels && "tw-justify-center tw-px-0"
                            )}
                        >
                            {showLabels && (
                                <>
                                    <span className="tw-truncate">
                                        {areAllKeysOpen ? t("collapse-all") : t("expand-all")}
                                    </span>
                                    {areAllKeysOpen ? (
                                        <ChevronUp className="tw-ml-auto tw-size-3.5" />
                                    ) : (
                                        <ChevronDown className="tw-ml-auto tw-size-3.5" />
                                    )}
                                </>
                            )}
                        </SidebarMenuButton>
                    </SidebarMenuItem>

                    {renderMenuItems(config)}
                </SidebarMenu>
            </SidebarContent>
        </SidebarPrimitive>
    );
}

export default Sidebar;
